import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Store, Lock, Mail, ArrowRight } from 'lucide-react';

export const TenantLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showBranchSelect, setShowBranchSelect] = useState(false);
  const [branches, setBranches] = useState([]);
  const { tenantLogin, selectBranch, loading } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    const result = await tenantLogin(email, password);
    if (result.success) {
      if (result.branches.length === 1) {
        navigate('/app/pos');
      } else {
        setBranches(result.branches);
        setShowBranchSelect(true);
      }
    } else {
      setError(result.error);
    }
  };

  const handleBranchSelect = async (branchId) => {
    const result = await selectBranch(branchId);
    if (result.success) {
      navigate('/app/pos');
    } else {
      setError(result.error);
    }
  };

  if (showBranchSelect) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1768095752732-6fba331e1a60?w=1920&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
        <Card className="w-full max-w-md relative z-10 bg-card/95 backdrop-blur">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold tracking-tight">Select Branch</CardTitle>
            <CardDescription>Choose your working branch</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {branches.map(branch => (
              <Button
                key={branch.id}
                variant="outline"
                className="w-full h-14 justify-between text-left"
                onClick={() => handleBranchSelect(branch.id)}
                data-testid={`branch-select-${branch.code}`}
              >
                <span>
                  <span className="font-semibold">{branch.name}</span>
                  <span className="text-muted-foreground ml-2">({branch.code})</span>
                </span>
                <ArrowRight className="w-5 h-5" />
              </Button>
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        backgroundImage: 'url(https://images.unsplash.com/photo-1768095752732-6fba331e1a60?w=1920&q=80)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <Card className="w-full max-w-md relative z-10 bg-card/95 backdrop-blur">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
            <Store className="w-8 h-8 text-primary" />
          </div>
          <div>
            <CardTitle className="text-3xl font-bold tracking-tight">PH Tablet POS</CardTitle>
            <CardDescription className="text-base mt-2">Sign in to your store</CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm" data-testid="login-error">
                {error}
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 pl-11"
                  required
                  data-testid="login-email"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 pl-11"
                  required
                  data-testid="login-password"
                />
              </div>
            </div>
            
            <Button 
              type="submit" 
              className="w-full h-14 text-base font-semibold"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
          
          <div className="mt-6 text-center">
            <Link 
              to="/platform/login" 
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              Platform Admin Login
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export const PlatformLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const { platformLogin, loading } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    const result = await platformLogin(email, password);
    if (result.success) {
      navigate('/platform/dashboard');
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
            <Lock className="w-8 h-8 text-primary" />
          </div>
          <div>
            <CardTitle className="text-3xl font-bold tracking-tight">Platform Admin</CardTitle>
            <CardDescription className="text-base mt-2">Super Admin Console</CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm" data-testid="platform-login-error">
                {error}
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@phpos.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 pl-11"
                  required
                  data-testid="platform-login-email"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 pl-11"
                  required
                  data-testid="platform-login-password"
                />
              </div>
            </div>
            
            <Button 
              type="submit" 
              className="w-full h-14 text-base font-semibold"
              disabled={loading}
              data-testid="platform-login-submit"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
          
          <div className="mt-6 text-center">
            <Link 
              to="/app/login" 
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              Tenant Login
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
